import React, { useEffect, useState } from 'react';
import { cargarMarcas } from '../../lib/marcas';
import { Label } from '../ui/label';

/**
 * «Marca de los mensajes al paciente» de una agenda y su candado.
 *
 * La marca (29/09/2026): la de Configuración → Marcas con la que se presenta
 * la agenda. Con ella, la confirmación y el recordatorio de sus citas llevan
 * el nombre, la dirección y el teléfono de la marca; sin ella (lo de siempre),
 * los de Configuración. Qué agenda es de qué marca lo decide la clínica,
 * agenda por agenda: no hay ninguna regla escrita en el código.
 *
 * El candado (30/09/2026): «No permitir marca» para una agenda de cirugía,
 * donde no interesa que se cambie por costumbre. Bloquear también QUITA la
 * marca que tuviera.
 *
 * Lo comparten Configurar agenda y Nueva agenda (30/09/2026), con las mismas
 * reglas que el servidor (`agendas_router._marca_de_la_agenda`).
 *
 * Props:
 *  - prefijo:        prefijo de ids y data-testid ('agenda-config', 'agenda-nueva')
 *  - marcaClave / onMarcaClave:         '' = sin marca
 *  - marcaBloqueada / onMarcaBloqueada
 */
export default function MarcaDeLaAgenda({
  prefijo, marcaClave, onMarcaClave, marcaBloqueada, onMarcaBloqueada,
}) {
  const [marcas, setMarcas] = useState([]);

  // Las marcas se piden al montarse, que es al abrir el diálogo (el contenido
  // de un diálogo cerrado no se monta). Solo las propias: una procedencia
  // externa no firma los mensajes de la clínica.
  useEffect(() => {
    let vivo = true;
    cargarMarcas().then((lista) => {
      if (vivo) setMarcas((Array.isArray(lista) ? lista : []).filter((m) => !m.externa));
    });
    return () => { vivo = false; };
  }, []);
  const marcaFueraDeLista = Boolean(marcaClave) && !marcas.some((m) => m.clave === marcaClave);
  const nombreDe = (clave) => {
    const m = marcas.find((x) => x.clave === clave);
    return m?.nombre_largo || m?.nombre || clave;
  };

  return (
    <div data-testid={`${prefijo}-marca`}>
      <Label htmlFor={`${prefijo}-marca-clave`}>Marca de los mensajes al paciente</Label>
      {marcaBloqueada ? (
        <p className="mt-1.5 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground"
           data-testid={`${prefijo}-marca-bloqueada-aviso`}>
          Bloqueada: esta agenda no admite marca. Quita el bloqueo de abajo para poder elegir una.
          {marcaClave && (
            <> Aún lleva la marca «{nombreDe(marcaClave)}»: sus mensajes
            salen con ella hasta que desbloquees y la quites.</>
          )}
        </p>
      ) : (
        <select
          id={`${prefijo}-marca-clave`}
          value={marcaClave}
          onChange={(e) => onMarcaClave(e.target.value)}
          className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
          data-testid={`${prefijo}-marca-clave`}
        >
          <option value="">Sin marca: los datos de la clínica (Configuración)</option>
          {marcas.map((m) => (
            <option key={m.clave} value={m.clave}>{m.nombre_largo || m.nombre}</option>
          ))}
          {marcaFueraDeLista && (
            <option value={marcaClave}>{marcaClave} (ya no está activa en Marcas)</option>
          )}
        </select>
      )}
      <p className="text-xs text-muted-foreground mt-1">
        Con marca, la confirmación y el recordatorio de las citas de esta agenda llevan el
        nombre, la dirección y el teléfono de la marca (Configuración → Marcas); lo que la
        marca no tenga, y todo en las agendas sin marca, sale de Configuración.
      </p>
      <label className="mt-2 flex items-center gap-2 text-sm text-muted-foreground" htmlFor={`${prefijo}-marca-bloqueada`}>
        <input
          id={`${prefijo}-marca-bloqueada`}
          type="checkbox"
          checked={marcaBloqueada}
          onChange={(e) => {
            // Bloquear también QUITA la marca que tuviera (revisión del
            // 30/9/2026): si no, el aviso «no admite marca» escondía que
            // sus mensajes seguían saliendo con ella.
            onMarcaBloqueada(e.target.checked);
            if (e.target.checked) onMarcaClave('');
          }}
          data-testid={`${prefijo}-marca-bloqueada`}
        />
        No permitir marca en esta agenda (para una de cirugía, donde no interesa que se
        cambie por costumbre)
      </label>
    </div>
  );
}
